<script lang="ts">
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { DateTime } from 'luxon'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { DOMAIN_EXPIRATION_IMMINENT, DOMAIN_EXPIRATION_SOON } from 'wdom/constants'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const LIMIT = 5

    interface Props {
        qText: string
    }

    let { qText }: Props = $props()

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    let results = writable([] as any[])

    let loading = $state(true)
    let updating = $state(false)
    let searchId = 0
    let searchQueryString: string = $state('')
    let totalItems: number = $state(0)
    function doSearch(text: string) {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (text != '') sdata.text = text
        searchQueryString = `q=${encodeURIComponent(qText)}`

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/domain/1', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
            if (loading) loading = false
        })(searchId)
    }

    let lastQs: string | undefined
    $effect(() => {
        // Doesn't really happen, but as a safety..
        if (qText && qText != lastQs) {
            lastQs = qText
            doSearch(qText)
        }
    })

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        totalItems = data.total_items
        const now = DateTime.now()

        for (const item of data.items) {
            for (const key of ['ctime', 'mtime']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }
            if (item.expiration) {
                item.expiration = toLuxon(item.expiration)
                if (item.expiration.diff(now) < DOMAIN_EXPIRATION_IMMINENT) {
                    item.expirationStatus = 'imminent'
                } else if (item.expiration.diff(now) < DOMAIN_EXPIRATION_SOON) {
                    item.expirationStatus = 'soon'
                } else {
                    item.expirationStatus = 'clean'
                }
            } else {
                item.expirationStatus = 'unknown'
            }

            item.idnaUnicode = toUnicode(item.idna)

            litems.push(item)
        }

        results.set(litems)
    }
</script>

<div class="card">
    <div class="card-title text-primary">{$_('Domains')}</div>

    <div class="card-body">
        <table class="table table-striped mb-0" class:disabled-look={updating}>
            <thead>
                <tr>
                    <th class="col-md-3">{$_('Name')}</th>
                    <th class="col-md-1 text-center"><Icon inline icon="fa:lock" /></th>
                    <th class="col-md-1 text-center"><Icon inline icon="fa:eye-slash" /></th>
                    <th class="col-md-3 text-center">{$_('Expiration')}</th>
                </tr>
            </thead>
            <tbody>
                {#if loading}
                    <tr>
                        <td colspan="4"><LoadingPattern pattern={['x']} /></td>
                    </tr>
                    <tr>
                        <td colspan="4"><LoadingPattern pattern={['x']} /></td>
                    </tr>
                {:else if !$results.length}
                    <tr>
                        <td colspan="4" class="text-center">Aucun résultat</td>
                    </tr>
                {:else}
                    {#each $results as item (item.idna)}
                        <tr>
                            <td><a use:sanchor href={spath(`/domain/${item.idna}`)}>{item.domain}.{item.tld}</a></td>
                            <td class="text-center"><Icon icon={item.locked ? 'fa:dot-circle-o' : 'fa:circle-o'} /></td>
                            <td class="text-center"><Icon icon={item.whois_privacy === true ? 'fa:eye' : item.whois_privacy === false ? 'fa:vide' : 'fa:minus'} /></td>
                            <td class="text-center"><Date value={item.expiration} format="h-table-s" /></td>
                        </tr>
                    {/each}
                {/if}
            </tbody>
        </table>
    </div>
    {#if totalItems != null}
        <div class="card-footer">
            <div class="float-end">{$_('Total : {n}', { n: totalItems })}</div>
            <a use:sanchor href={spath(`/domains?${searchQueryString}`)}>{$_('All results')}</a>
        </div>
    {/if}
</div>
